(() => {
  if (window.__ottoRecommendationsInit) return;
  window.__ottoRecommendationsInit = true;

  const ROOT_MARGIN = '0px 0px 200px 0px';

  const publish = (eventName, payload) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(Object.assign({ event: eventName }, payload));
    if (window.Shopify?.analytics?.publish) {
      window.Shopify.analytics.publish(eventName, payload);
    }
  };

  const parseDocument = (html) =>
    new DOMParser().parseFromString(html, 'text/html');

  const findIncoming = (html, selector) => {
    const doc = parseDocument(html);
    const section = doc.querySelector('.shopify-section') || doc.body;
    if (!selector) return section;
    return section.querySelector(selector) || doc.querySelector(selector);
  };

  const hasCards = (node) =>
    !!(
      node &&
      node.querySelector(
        '[data-bundle-ready], .recs-track__item, product-card, .akqa-swiper-slide'
      )
    );

  const fetchHtml = async (url) => {
    const response = await fetch(url);
    if (!response.ok) throw new Error('Recommendation request failed');
    return response.text();
  };

  const formatMoney = (cents) => {
    const currency = document.documentElement.dataset.currencyCode || 'DKK';
    const locale = currency === 'SEK' ? 'sv-SE' : 'da-DK';
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      currencyDisplay: currency === 'SEK' ? 'code' : 'symbol',
    }).format((Number(cents) || 0) / 100).replace(/[\u00a0\u202f]/g, ' ');
  };

  const selectedInputs = (root) =>
    Array.from(root.querySelectorAll('[data-bundle-row] input')).filter(
      (input) => input.checked || input.dataset.locked === 'true'
    );

  const updateBundleSummary = (root) => {
    const host = root.closest('product-bundle-suggestion') || root;
    const rows = selectedInputs(root);
    const mixQty = Number(host.dataset.mixQty || 6);
    const mixPercent = Number(host.dataset.mixPercent || 10);
    const serverPricingOnly = host.dataset.serverPricingOnly === 'true';
    let subtotal = 0;
    let qty = 0;
    let allMix = rows.length > 0;

    rows.forEach((input) => {
      const lineQty = Number(input.dataset.qty || 1);
      subtotal += Number(input.dataset.price || 0) * lineQty;
      qty += lineQty;
      if (input.dataset.mix !== 'true') allMix = false;
    });

    const applyMix = allMix && qty >= mixQty && mixPercent > 0;
    const saving = !serverPricingOnly && applyMix ? Math.round((subtotal * mixPercent) / 100) : 0;
    const total = subtotal - saving;
    const totalEl = root.querySelector('[data-bundle-total]');
    const compareEl = root.querySelector('[data-bundle-compare]');
    const saveEl = root.querySelector('[data-bundle-save]');
    const saveLabel = root.querySelector('[data-bundle-save-label]');
    const saveAmount = root.querySelector('[data-bundle-save-amount]');
    const addBtn = root.querySelector('[data-bundle-add]');
    const shippingEl = root.querySelector('[data-bundle-shipping]');
    const linesEl = root.querySelector('[data-bundle-lines]');
    const shippingThreshold = Number(host.dataset.freeShipping || 99900);
    const payable = applyMix && saving > 0 ? total : subtotal;
    const qtyTemplate = host.dataset.i18nLineQty || '__COUNT__';

    if (linesEl) {
      const nextLines = document.createDocumentFragment();
      rows.forEach((input) => {
        const lineQty = Number(input.dataset.qty || 1);
        const li = document.createElement('li');
        li.className = 'product-bundle__line';
        const name = document.createElement('span');
        name.className = 'product-bundle__line-name';
        name.textContent = input.dataset.title || '';
        const qtyEl = document.createElement('span');
        qtyEl.className = 'product-bundle__line-qty';
        qtyEl.textContent = qtyTemplate.split('__COUNT__').join(String(lineQty));
        li.append(name, qtyEl);
        nextLines.append(li);
      });
      linesEl.replaceChildren(nextLines);
    }

    if (!serverPricingOnly && applyMix && saving > 0) {
      if (compareEl) {
        compareEl.hidden = false;
        compareEl.textContent = formatMoney(subtotal);
      }
      if (totalEl) totalEl.textContent = formatMoney(total);
    } else {
      if (compareEl) {
        compareEl.hidden = true;
        compareEl.textContent = '';
      }
      if (totalEl) totalEl.textContent = formatMoney(subtotal);
    }

    if (saveEl) {
      if (!serverPricingOnly && applyMix && saving > 0) {
        const template = host.dataset.i18nSave || '';
        if (saveLabel) {
          saveLabel.textContent = template.split('__BOTTLES__').join(String(qty));
        }
        if (saveAmount) saveAmount.textContent = `−${formatMoney(saving)}`;
        saveEl.hidden = false;
      } else {
        saveEl.hidden = true;
      }
    }

    if (shippingEl) {
      shippingEl.hidden = payable < shippingThreshold;
    }

    if (addBtn) {
      addBtn.disabled = rows.length < 2;
      if (!serverPricingOnly && applyMix && saving > 0) {
        addBtn.textContent = host.dataset.i18nAddSave || addBtn.textContent;
      } else {
        addBtn.textContent = host.dataset.i18nAddPlain || addBtn.textContent;
      }
    }
  };

  let lastUndo = null;

  const parseSectionHtml = (html) =>
    parseDocument(html).querySelector('.shopify-section')?.innerHTML ?? '';

  const replaceSectionElement = (el, sectionHtml) => {
    const html = parseSectionHtml(sectionHtml);
    if (!html || !el) return;
    if (el.previousElementSibling) {
      el.insertAdjacentHTML('afterend', html);
      el.remove();
    } else if (el.nextElementSibling) {
      el.insertAdjacentHTML('beforebegin', html);
      el.remove();
    } else if (el.parentElement) {
      el.parentElement.innerHTML = html;
    }
  };

  const replaceCartSections = (sections) => {
    if (!sections) {
      refreshCart();
      return;
    }
    ['shopping-cart', 'cart-amount', 'cart-recommendations'].forEach((name) => {
      if (!document.querySelector(name)) return;
      const parsed = parseDocument(sections[name] || '');
      if (!parsed.querySelector(`.shopify-section ${name}`)) throw new Error(`Invalid cart section: ${name}`);
    });
    if (sections['shopping-cart']) {
      document.querySelectorAll('shopping-cart').forEach((shoppingCart) => {
        replaceSectionElement(shoppingCart, sections['shopping-cart']);
      });
    }
    document.querySelectorAll('cart-amount').forEach((cartAmount) => {
      if (sections['cart-amount']) replaceSectionElement(cartAmount, sections['cart-amount']);
    });
    if (sections['cart-recommendations']) {
      document.querySelectorAll('cart-recommendations').forEach((recs) => {
        if (sections['shopping-cart'] && recs.closest('shopping-cart')) return;
        replaceSectionElement(recs, sections['cart-recommendations']);
      });
    }
    const related = document.querySelector('#cart-related-recommendations');
    if (related && sections['cart-related-recommendations']) {
      replaceSectionElement(related, sections['cart-related-recommendations']);
    }
    ['__ottoUpdateExpectedDelivery', '__ottoInitFreeShipping'].forEach((name) => {
      try { window[name]?.(); } catch (error) { console.warn(`${name} failed after cart update`, error); }
    });
    window.dispatchEvent(new CustomEvent('cart:updated'));
    scan(document);
  };

  window.__ottoReplaceCartSections = replaceCartSections;

  const refreshCart = () => {
    const names = ['shopping-cart', 'cart-amount', 'cart-recommendations', 'cart-related-recommendations'];
    fetch(`${window.location.pathname}?sections=${names.join(',')}`)
      .then((response) => response.json())
      .then(replaceCartSections)
      .catch(() => {});
  };

  const openCart = () => {
    const cartBtn = document.querySelector('#header [data-element="cart-btn"]');
    if (!cartBtn) return;
    window.dispatchEvent(
      new CustomEvent('cart:toggle', { detail: { element: cartBtn, skipCartDefer: true } })
    );
  };

  const bindBundle = (scope) => {
    if (!scope) return;
    const roots = [];
    if (scope.matches?.('[data-bundle-root]')) roots.push(scope);
    scope.querySelectorAll?.('[data-bundle-root]').forEach((node) => roots.push(node));

    roots.forEach((root) => {
      if (root.dataset.bundleBound === '1') return;
      root.dataset.bundleBound = '1';
      const host = root.closest('product-bundle-suggestion') || root;
      root.addEventListener('change', () => updateBundleSummary(root));
      updateBundleSummary(root);

      const addBtn = root.querySelector('[data-bundle-add]');
      const undoBtn = root.querySelector('[data-bundle-undo]');
      const status = root.querySelector('[data-bundle-status]');

      addBtn?.addEventListener('click', async () => {
        const rows = selectedInputs(root);
        if (rows.length < 2) return;
        const items = rows.map((input) => ({
          id: Number(input.dataset.variantId),
          quantity: Number(input.dataset.qty || 1),
        }));
        addBtn.disabled = true;
        if (status) {
          status.hidden = false;
          status.textContent = host.dataset.i18nAdding || '';
        }
        try {
          const before = await fetch('/cart.js').then((response) => response.json());
          const addUrl = host.dataset.addUrl || '/cart/add.js';
          const response = await fetch(addUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
            body: JSON.stringify({
              items,
              sections: 'shopping-cart,cart-amount,cart-recommendations,cart-related-recommendations',
              sections_url: window.location.pathname,
            }),
          });
          if (!response.ok) throw new Error('Bundle add failed');
          const data = await response.json();
          lastUndo = { before, items };
          replaceCartSections(data.sections);
          openCart();
          const value =
            rows.reduce(
              (sum, input) =>
                sum + Number(input.dataset.price || 0) * Number(input.dataset.qty || 1),
              0
            ) / 100;
          publish('bundle_add_to_cart', {
            items: items.map((item, index) => ({
              id: item.id,
              quantity: item.quantity,
              title: rows[index].dataset.title,
            })),
            value,
          });
          if (status) status.textContent = host.dataset.i18nAdded || '';
          if (undoBtn) undoBtn.hidden = false;
        } catch (error) {
          console.warn(error);
          if (status) status.textContent = host.dataset.i18nError || '';
        } finally {
          addBtn.disabled = false;
          updateBundleSummary(root);
        }
      });

      undoBtn?.addEventListener('click', async () => {
        if (!lastUndo) return;
        const updates = {};
        lastUndo.before.items.forEach((item) => {
          updates[item.variant_id] = item.quantity;
        });
        lastUndo.items.forEach((item) => {
          if (updates[item.id] == null) updates[item.id] = 0;
        });
        await fetch('/cart/update.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({ updates }),
        });
        lastUndo = null;
        undoBtn.hidden = true;
        if (status) {
          status.textContent = '';
          status.hidden = true;
        }
        refreshCart();
      });
    });
  };

  const loadRecs = async (el) => {
    if (!el || el.dataset.recsLoaded === '1') return;
    el.dataset.recsLoaded = '1';
    const url = el.dataset.url;
    const fallback = el.dataset.fallbackUrl;
    const hostSelector = el.dataset.recsHost;
    if (!url) return;

    const apply = (incoming) => {
      if (!incoming || !hasCards(incoming)) return false;
      el.innerHTML = incoming.innerHTML;
      bindBundle(el);
      return true;
    };

    const applySimilarTaste = (html) => {
      const doc = parseDocument(html);
      const items = Array.from(doc.querySelectorAll('.recs-track__item')).filter(
        (item) => item.dataset.productId !== el.dataset.excludeId
      );
      const minCount = Number(el.dataset.minCount || 3);
      if (items.length < minCount) return false;
      const target = el.querySelector('[data-similar-target]');
      if (!target) return false;
      target.innerHTML = items
        .slice(0, 4)
        .map((item) => item.outerHTML)
        .join('');
      el.hidden = false;
      el.classList.add('is-ready');
      return true;
    };

    try {
      const html = await fetchHtml(url);
      if (el.dataset.recsKind === 'similar-taste') {
        if (applySimilarTaste(html)) return;
      } else if (apply(findIncoming(html, hostSelector))) {
        return;
      }
      if (fallback) {
        const fallbackHtml = await fetchHtml(fallback);
        if (apply(findIncoming(fallbackHtml, hostSelector))) {
          el.dataset.usedFallback = 'true';
          document
            .getElementById('cart-related-recommendations')
            ?.setAttribute('hidden', '');
          return;
        }
      }
    } catch (error) {
      console.warn(error);
    }

    el.classList.add('is-empty');
    const section = el.closest('.shopify-section');
    if (
      section &&
      (el.matches('product-bundle-suggestion') ||
        el.matches('product-similar-taste'))
    ) {
      section.hidden = true;
    }
  };

  const observe = (el) => {
    if (!el || el.dataset.recsObserved === '1') return;
    if (el.hasAttribute('data-recs-mobile') && window.matchMedia('(min-width: 1280px)').matches) return;
    el.dataset.recsObserved = '1';
    if (hasCards(el)) {
      el.dataset.recsLoaded = '1';
      bindBundle(el);
      return;
    }
    if (el.hasAttribute('data-recs-eager') || el.closest('#cart-dialog')) {
      loadRecs(el);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (!entries[0]?.isIntersecting) return;
        io.disconnect();
        loadRecs(el);
      },
      { rootMargin: ROOT_MARGIN }
    );
    io.observe(el);
  };

  const scan = (root = document) => {
    root.querySelectorAll?.('[data-recs-lazy]').forEach(observe);
    if (root.matches?.('[data-recs-lazy]')) observe(root);
    root.querySelectorAll?.('[data-bundle-root]').forEach((node) => bindBundle(node));
    if (root.matches?.('[data-bundle-root]')) bindBundle(root);
  };

  window.__ottoRecommendations = {
    scan,
    load: loadRecs,
  };

  document.addEventListener('click', (event) => {
    const link = event.target.closest?.('[data-rec-module] a[href]');
    if (!link) return;
    const moduleName = link.closest('[data-rec-module]')?.dataset.recModule;
    publish('recommendation_click', {
      module: moduleName,
      href: link.getAttribute('href'),
      text: (link.textContent || '').trim().slice(0, 120),
    });
  });

  scan();
  window.addEventListener('cart:toggle', () => {
    window.requestAnimationFrame(() => {
      document.querySelectorAll('#cart-dialog [data-recs-lazy]').forEach((el) => {
        if (el.dataset.recsLoaded === '1') return;
        el.dataset.recsObserved = '1';
        loadRecs(el);
      });
    });
  });
  document.addEventListener('shopify:section:load', (event) => {
    scan(event.target);
  });
  document.addEventListener('otto:recommendations:scan', (event) => {
    scan(event.detail?.root || document);
  });
})();
